import React from 'react'
import mergeWith from 'lodash/mergeWith'
import isString from 'lodash/isString'
import trim from 'lodash/trim'
import {
  info, success, warning, error, clear,
} from '@active/react-ui/FloatAlert'
import qs from 'querystring'
import DOMPurify from 'dompurify'
import I18nMessage from '../../components/common/I18nMessage'
import { getRootElement } from '../../shared/utils/poperConfig'

export const LOCAL_PREFIX = 'passport.ui.app.'
const VALID_RETURN_URL_DOMAINS = ['*.active.com','*.activenetwork.com']

export const noop = () => {
  // empty
}
export const identity = r => r

export const getI18nMessage = id => <I18nMessage id={`${id}`} />

export const getYear = () => {
  const myDate = new Date()
  return myDate
    .getFullYear()
    .toString()
}

export function interpolate(...args) {
  const str = args.shift()
  let context = args[0]
  const toString = Object.prototype.toString // eslint-disable-line

  /* eslint-disable no-nested-ternary */
  context = args.length === 1
    ? context !== null
      && /[object Array]|[object Object]/.test(Object.prototype.toString.call(context))
      ? context
      : args
    : args
  return str.replace(/{([\s\S]+?)}/g, (match, interpolateStr) => {
    let replacer = interpolateStr
      .split('.')
      .reduce((acc, k) => (acc ? acc[k] : acc), context || {})
    if (toString.call(replacer) === '[object Function]') {
      replacer = replacer(interpolateStr)
    }
    /* eslint-disable no-void */
    return replacer === null || replacer === void 0 ? '' : replacer
  })
}

export const getUrlParams = (url = window.location.search) => {
  let params = {}
  let queryStr = url
  const index = queryStr.indexOf('?')
  if (index > -1) {
    queryStr = url.substring(queryStr.indexOf('?') + 1)
  }
  if (queryStr) {
    params = qs.parse(queryStr)
  }

  return params
}

export const getUrlParameter = name => getUrlParams()[name]

export const getSafeReturnUrl = rawUrl => {
  if (!rawUrl || rawUrl === 'undefined') {
    return null
  }

  let decodedUrl
  try {
    decodedUrl = decodeURIComponent(rawUrl)
  } catch (error) {
    return null
  }

  try {
    const parsedUrl = new URL(decodedUrl, window.location.origin)
    const isAllowedProtocol = ['http:', 'https:'].includes(parsedUrl.protocol)
    const isAllowedDomain = VALID_RETURN_URL_DOMAINS.some(domain => {
      const normalizedDomain = domain.replace(/^\*\./, '')
      return parsedUrl.hostname === normalizedDomain
        || parsedUrl.hostname.endsWith(`.${normalizedDomain}`)
    })

    return isAllowedProtocol && isAllowedDomain ? decodedUrl : null
  } catch (error) {
    return null
  }
}

export const merge = (...args) => mergeWith({}, ...args)

const alerts = {
  info, success, warning, error,
}

export const messageUtils = (() => {
  const ALERT_DEFAULT_CONFIG = {
    timeout: 10000,
    theme: 'inline',
  }
  const acceptAlterTypes = ['success', 'warning', 'error', 'info']

  let target = null

  const alert = (message, { pureString, type, ...config } = {}) => {
    if (!target) {
      target = document.createElement('div')
      target.className = 'passport-ui-app__alert-target'
      const popperRoot = getRootElement() || document.body
      popperRoot.appendChild(target)
    }
    const _type = acceptAlterTypes.includes(type) ? type : 'info'

    const rawMessage = decodeURIComponent(message)
    const safeMessage = DOMPurify.sanitize(rawMessage, {
      SAFE_FOR_TEMPLATES: true,
    })

    alerts[_type](merge(ALERT_DEFAULT_CONFIG, { ...config }, {
      message: pureString ? message : typeof message === 'string' ? (
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: safeMessage }}
        />
      ) : message,
    }), target)
  }
  alert.clear = clear
  return {
    alert,
    alertError: message => alert(message, { type: 'error' }),
  }
})()

export const appendScript = config => {
  const {
    src, allowedExist, onload, alreadyLoad, async,
  } = config

  const existScript = Array.from(document.scripts).filter(s => s.src === src)[0]
  if (!allowedExist && existScript) {
    alreadyLoad && alreadyLoad()
    return existScript
  }

  const targetScript = document.createElement('script')
  targetScript.type = 'text/javascript'
  if (targetScript.readyState) { // IE
    targetScript.onreadystatechange = () => {
      if (targetScript.readyState === 'loaded'
        || targetScript.readyState === 'complete') {
        targetScript.onreadystatechange = null
        onload && onload()
      }
    }
  } else { // Non IE
    targetScript.onload = () => {
      onload && onload()
    }
  }

  targetScript.src = src
  if (async) {
    targetScript.async = true
  }
  return document.head.appendChild(targetScript)
}

export const omit = (obj, omitKeys = []) => {
  const result = {}
  const keys = Object.keys(obj)

  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]

    // If not found in the `omitKeys`
    if (omitKeys.indexOf(key) === -1) {
      result[key] = obj[key]
    }
  }

  return result
}

// eslint-disable-next-line no-restricted-globals
export const filterDate = date => ((date && !isNaN(date.getTime())) ? date : null)

export const calculateAge = dob => {
  const ageDt = new Date(Date.now() - dob.getTime())

  return Math.abs(ageDt.getUTCFullYear() - 1970)
}

export function countryCodeToCountry(ob) {
  const result = ob
  if (ob != null && typeof ob === 'object') {
    const keys = Object.keys(ob)

    keys.forEach(key => {
      if (key === 'countryCode') {
        result.country = ob[key]
      }
      result[key] = countryCodeToCountry(ob[key])
    })
  }
  return result
}

export function countryToCountryCode(ob) {
  const result = ob
  if (ob != null && typeof ob === 'object') {
    const keys = Object.keys(ob)

    keys.forEach(key => {
      if (key === 'country') {
        result.countryCode = ob[key]
      }
      result[key] = countryToCountryCode(ob[key])
    })
  }
  return result
}

export const sortbyBuilder = sorters => (a, b) => {
  if (Array.isArray(sorters)) {
    const results = []
    sorters.forEach(sorter => {
      if (sorter.field(a) < sorter.field(b)) {
        results.push(sorter.asc ? -1 : 1)
      } else if (sorter.field(a) > sorter.field(b)) {
        results.push(sorter.asc ? 1 : -1)
      } else {
        results.push(0)
      }
    })
    const res = results.find(result => result !== 0)
    return res == null ? 0 : res
  }
  return 0
}

export function isTrue(value) {
  return (/^true$/i).test(value)
}

export function reduceFalseProp(ob) {
  const result = {}
  Object.entries(ob).forEach(([key, value]) => {
    if (value) {
      result[key] = value
    }
  })
  return result
}

export function reduceBlankProp(ob) {
  const result = {}
  Object.entries(ob).forEach(([key, value]) => {
    if (value != null && value !== '') {
      result[key] = value
    }
  })
  return result
}

/**
 * @description handle keyboard event with specific key/keys
 * @param handler {Function}
 * @param keys { 'Enter' | 'Esc' | ' ' | ['Enter', 'Esc', ' '] | string | string[]}
 * @returns {(function(e: KeyboardEvent ): void)|*}
 */
export const handleKeyboardEvent = (handler, keys, isPreventDefault = true) => e => {
  if (!keys || keys.length === 0) return
  const _keys = Array.isArray(keys) ? keys : [keys]
  if (e.key && _keys.includes(e.key)) {
    handler(e)
    isPreventDefault && e.preventDefault()
  }
}

export function maskSenderEmail(senderEmail) {
  if (!senderEmail) return senderEmail
  const MASKER = '******'
  const [toBeMask, suffix] = senderEmail.split('@')
  const masked = `${toBeMask.substring(0, 2)}${MASKER}`
  return [masked, suffix].join('@')
}

export function maskSenderPhone(senderPhone) {
  if (!senderPhone || (typeof senderPhone !== 'string' && typeof senderPhone !== 'number')) return senderPhone
  const MASKER = '****'
  const phoneStr = `${senderPhone}`
  if (phoneStr.length < 7) {
    return `${phoneStr.substring(0, 3)}${MASKER}`
  }
  return `${phoneStr.substring(0, 3)}${MASKER}${phoneStr.substring(phoneStr.length - 3, phoneStr.length)}`
}

const SUPPORT_LOCALES = [
  'ca_ES',
  'cy_GB',
  'da_DK',
  'de_AT',
  'de_CH',
  'de_DE',
  'en_AU',
  'en_CA',
  'en_GB',
  'en_IE',
  'en_NZ',
  'en_SG',
  'en_US',
  'en_ZA',
  'es_ES',
  'es_MX',
  'es_US',
  'fr_CA',
  'fr_CH',
  'fr_FR',
  'it_CH',
  'it_IT',
  'ja_JP',
  'ko_KR',
  'ms_MY',
  'nl_NL',
  'no_NO',
  'ph_PH',
  'pl_PL',
  'pt_BR',
  'sv_SE',
  'th_TH',
  'zh_CN',
  'zh_HK',
  'zh_SG',
  'zh_TW',
]

const LOCALE_MAP = {
  ca: 'ca_ES',
  cy: 'cy_GB',
  da: 'da_DK',
  de: 'de_DE',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  it: 'it_IT',
  ja: 'ja_JP',
  ko: 'ko_KR',
  ms: 'ms_MY',
  nl: 'nl_NL',
  no: 'no_NO',
  ph: 'ph_PH',
  pl: 'pl_PL',
  pt: 'pt_BR',
  sv: 'sv_SE',
  th: 'th_TH',
  zh: 'zh_CN',
}

export function adjustLocale(locale) {
  if (SUPPORT_LOCALES.includes(locale)) {
    return locale
  }
  if (Object.keys(LOCALE_MAP).includes(locale)) {
    return LOCALE_MAP[locale]
  }
  return 'en_US'
}

export const isEmptyStrOrWhiteSpace = str => isString(str) && trim(str).length === 0

// Build safe query string to prevent XSS
export function buildSafeQueryString(locale) {
  const params = new URLSearchParams(window.location.search)
  const safeParams = new URLSearchParams()

  // Add all other URL parameters with sanitization
  params.forEach((value, param) => {
    // Skip locale
    if (value != null && value !== undefined) {
      // Sanitize: remove any HTML/JS characters
      const sanitizedValue = value.replace(/[<>"'`]/g, '')
      if (sanitizedValue != null && sanitizedValue !== undefined && sanitizedValue !== '') {
        safeParams.set(param, sanitizedValue)
      }
    }
  })

  // Always use locale from props (validated) to avoid override from URL
  const safeLocale = locale.match(/^[a-zA-Z_-]+$/) ? locale : 'en_US'
  safeParams.set('locale', safeLocale)

  return `?${safeParams.toString()}`
}